import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, animate, motion, useMotionValue } from "motion/react";
import { HiX } from "react-icons/hi";
import ConversationView from "./ConversationView";
import UserAvatar from "../ui/UserAvatar";
import { useChat } from "./ChatProvider";
import { useIsMobile } from "./useMedia";
import { useFullScreenView } from "../../utils/viewport";
import { useBackToClose } from "../../utils/backToClose";
import { otherMemberId } from "./chatUtils";

// Chat heads behave like a phone's: each one is dragged on its own and stays exactly where it's dropped
// (pulled back inside the screen if dropped partly off it), or can be dropped on the close target at
// the bottom of the screen. New heads stack up the right edge. Positions are remembered per browser.

const POSITIONS_KEY = "irg-chat-head-positions";
const EDGE = 16;
const HEAD_SIZE = 56;
const SLOT = HEAD_SIZE + 12; // spacing between stacked heads
const MARGIN = 8; // a dropped head is kept at least this far inside the screen
const GAP = 12;
const WINDOW_HEIGHT = 520;
const DRAG_THRESHOLD = 5;
const CLOSE_TARGET_SIZE = 64;
const CLOSE_TARGET_BOTTOM = 32;
const CLOSE_RADIUS = 90; // px from the target's centre where a head is pulled onto it
// Smooth ease-out, no overshoot: things glide to a stop instead of bouncing.
const SMOOTH = { type: "tween", ease: [0.22, 1, 0.36, 1], duration: 0.22 };
const MAX_REMEMBERED = 30;
// Phones: room for the bottom tab bar (ui/MobileNav.jsx) plus the home indicator.
const TAB_BAR_CLEARANCE = 56 + 34 + MARGIN;

const readPositions = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(POSITIONS_KEY));
    return saved && typeof saved === "object" ? saved : {};
  } catch {
    return {};
  }
};

const savePositions = (positions) => {
  try {
    localStorage.setItem(POSITIONS_KEY, JSON.stringify(positions));
  } catch {
    /* not remembered this time */
  }
};

const useViewportSize = () => {
  const [size, setSize] = useState(() => ({ width: window.innerWidth, height: window.innerHeight }));
  useEffect(() => {
    const onResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return size;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

// A position is { side, offset, bottom }: `offset` is the distance from the nearer side edge, so a head
// near the right edge stays near it when the window is resized. This keeps it fully on screen.
// `minBottom` keeps heads clear of the phone tab bar.
const placeOnScreen = (pos, vw, vh, minBottom = MARGIN) => ({
  side: pos.side,
  offset: clamp(Number.isFinite(pos.offset) ? pos.offset : EDGE, MARGIN, vw - HEAD_SIZE - MARGIN),
  bottom: clamp(pos.bottom, minBottom, vh - HEAD_SIZE - MARGIN),
});
const leftOf = (pos, vw) => (pos.side === "left" ? pos.offset : vw - pos.offset - HEAD_SIZE);

// The lowest free spot up the right edge, for a head that hasn't been placed yet.
function freeRightEdgeSpot(taken, vw, vh, minBottom = EDGE) {
  const left = vw - EDGE - HEAD_SIZE;
  for (let bottom = Math.max(EDGE, minBottom); bottom <= vh - HEAD_SIZE - MARGIN; bottom += 8) {
    if (taken.every((t) => Math.abs(leftOf(t, vw) - left) >= SLOT || Math.abs(t.bottom - bottom) >= SLOT)) {
      return { side: "right", offset: EDGE, bottom };
    }
  }
  return { side: "right", offset: EDGE, bottom: Math.max(EDGE, minBottom) };
}

// Positions for the open heads: remembered spots as they were left, new heads stacked up the right edge.
function layoutHeads(ids, saved, vw, vh, minBottom) {
  const result = {};
  const taken = [];
  for (const id of ids) {
    const pos = saved[id];
    if (pos && (pos.side === "left" || pos.side === "right") && Number.isFinite(pos.bottom)) {
      result[id] = placeOnScreen(pos, vw, vh, minBottom);
      taken.push(result[id]);
    }
  }
  for (const id of ids) {
    if (result[id]) continue;
    result[id] = freeRightEdgeSpot(taken, vw, vh, minBottom);
    taken.push(result[id]);
  }
  return result;
}

const closeTargetCenter = () => ({ x: window.innerWidth / 2, y: window.innerHeight - CLOSE_TARGET_BOTTOM - CLOSE_TARGET_SIZE / 2 });

// One open chat as a round "chat head": photo, unread / @ badge, typing dots, name on hover, × to close.
// It's placed purely by its x/y motion values (screen coordinates of its top-left corner), so a drop
// needs no re-anchoring: the head is already where it was released and simply stays there.
function ChatHead({ conversationId, expanded, position, viewport, onToggle, onClose, onDragStart, onDragMove, onDrop, tourTarget }) {
  const { conversations, users, me, typing, titleOf } = useChat();
  const [dragging, setDragging] = useState(false);
  const left = leftOf(position, viewport.width);
  const top = viewport.height - position.bottom - HEAD_SIZE;
  const x = useMotionValue(left);
  const y = useMotionValue(top);
  const swallowClickRef = useRef(false);
  const draggingRef = useRef(false);
  const glideRef = useRef(false);
  const magnetRef = useRef(false);

  // Follow the saved position: after a drop that had to be pulled back on screen, glide there smoothly;
  // otherwise (resize, layout of new heads) just be there.
  useLayoutEffect(() => {
    if (draggingRef.current) return;
    if (glideRef.current) {
      glideRef.current = false;
      if (Math.abs(x.get() - left) > 0.5 || Math.abs(y.get() - top) > 0.5) {
        animate(x, left, SMOOTH);
        animate(y, top, SMOOTH);
      }
      return;
    }
    x.jump(left);
    y.jump(top);
  }, [left, top, x, y]);

  const onPointerDown = (e) => {
    if (e.button !== 0 || e.target.closest('[aria-label^="Close chat"]')) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = x.get();
    const startTop = y.get();
    const { pointerId } = e;
    let active = false;

    const onMove = (ev) => {
      if (ev.pointerId !== pointerId) return;
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (!active) {
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
        active = true;
        draggingRef.current = true;
        setDragging(true);
        onDragStart(conversationId);
      }
      const overClose = onDragMove({ x: startLeft + dx + HEAD_SIZE / 2, y: startTop + dy + HEAD_SIZE / 2 });
      if (overClose) {
        // Pulled onto the close target.
        if (!magnetRef.current) {
          magnetRef.current = true;
          navigator.vibrate?.(15);
          const target = closeTargetCenter();
          animate(x, target.x - HEAD_SIZE / 2, SMOOTH);
          animate(y, target.y - HEAD_SIZE / 2, SMOOTH);
        }
      } else {
        if (magnetRef.current) {
          magnetRef.current = false;
          x.stop();
          y.stop();
        }
        x.set(startLeft + dx);
        y.set(startTop + dy);
      }
    };

    const onUp = (ev) => {
      if (ev.pointerId !== pointerId) return;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      if (!active) return;
      swallowClickRef.current = true;
      setTimeout(() => {
        swallowClickRef.current = false;
      }, 0);
      const closing = magnetRef.current;
      magnetRef.current = false;
      draggingRef.current = false;
      glideRef.current = !closing;
      setDragging(false);
      onDrop(conversationId, { center: { x: x.get() + HEAD_SIZE / 2, y: y.get() + HEAD_SIZE / 2 }, closing });
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const conversation = conversations[conversationId];
  // While animating out after the chat was deleted or left, the conversation is already gone.
  if (!conversation) return null;
  const isGroup = conversation.type === "group";
  const other = isGroup ? null : users[otherMemberId(conversation, me?.id)];
  const title = titleOf(conversation);
  const typers = Object.keys(typing[conversationId] || {}).filter((id) => Number(id) !== me?.id);
  const unread = conversation.unreadCount || 0;
  const { side } = position;

  return (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: dragging ? 1.12 : 1, opacity: 1 }}
      exit={{ scale: 0, opacity: 0 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      style={{ left: 0, top: 0, x, y, touchAction: "none" }}
      onPointerDown={onPointerDown}
      onClickCapture={(e) => {
        if (swallowClickRef.current) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      className={`group fixed select-none ${dragging ? "z-[63] cursor-grabbing" : "z-[61]"}`}
      data-chat-head={conversationId}
      data-chat-head-side={side}
      data-tour={tourTarget ? "chat-heads" : undefined}
    >
      {!dragging && (
        <span
          className={`pointer-events-none absolute bottom-full ${side === "left" ? "left-0" : "right-0"} mb-2 max-w-[240px] truncate whitespace-nowrap rounded-lg bg-gray-900/90 px-2 py-1 text-xs font-medium text-white opacity-0 shadow transition-opacity group-hover:opacity-100`}
        >
          {title}
        </span>
      )}
      <button
        type="button"
        onClick={onToggle}
        aria-label={`${expanded ? "Collapse" : "Open"} chat with ${title}${unread ? `, ${unread} unread` : ""}`}
        aria-pressed={expanded}
        title={dragging ? undefined : "Click to open · drag to move"}
        className={`block rounded-full transition-[transform,box-shadow] duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-300 ${
          dragging ? "shadow-2xl" : "shadow-lg hover:scale-105"
        } ${expanded ? "ring-[3px] ring-[#2470B7] ring-offset-2 ring-offset-white dark:ring-offset-gray-900" : ""}`}
      >
        <UserAvatar
          name={title}
          src={isGroup ? conversation.avatarThumbUrl : other?.avatarThumbUrl}
          seed={isGroup ? conversation.id : other?.id}
          group={isGroup}
          size="lg"
          online={isGroup ? undefined : !!other?.online}
        />
      </button>
      {conversation.mentionCount > 0 ? (
        <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-white dark:ring-gray-900" title="You were mentioned">
          @
        </span>
      ) : (
        unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-white dark:ring-gray-900">
            {unread > 99 ? "99+" : unread}
          </span>
        )
      )}
      {typers.length > 0 && (
        <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5 rounded-full bg-white dark:bg-gray-800 px-1.5 py-1 shadow" aria-label="typing">
          {[0, 150, 300].map((delay) => (
            <span key={delay} className="w-1 h-1 rounded-full bg-gray-500 animate-bounce" style={{ animationDelay: `${delay}ms` }} />
          ))}
        </span>
      )}
      {!dragging && (
        <button
          type="button"
          onClick={onClose}
          aria-label={`Close chat with ${title}`}
          className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-gray-700 text-white flex items-center justify-center opacity-0 shadow transition-opacity group-hover:opacity-100 focus:opacity-100 hover:bg-gray-900"
        >
          <HiX className="w-3 h-3" />
        </button>
      )}
    </motion.div>
  );
}

// Shown while a head is dragged: drop it on the × to close that chat.
function CloseTarget({ active }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[62] h-48 bg-gradient-to-t from-black/35 to-transparent"
      data-chat-close-target
      data-active={active ? "true" : "false"}
    >
      <motion.div
        initial={{ y: 40, scale: 0.8 }}
        animate={{ y: 0, scale: active ? 1.25 : 1 }}
        exit={{ y: 40, scale: 0.8 }}
        transition={SMOOTH}
        style={{ width: CLOSE_TARGET_SIZE, height: CLOSE_TARGET_SIZE, bottom: CLOSE_TARGET_BOTTOM, marginLeft: -CLOSE_TARGET_SIZE / 2 }}
        className={`absolute left-1/2 flex items-center justify-center rounded-full border-2 text-white shadow-xl transition-colors ${
          active ? "border-red-300 bg-red-500" : "border-white/70 bg-gray-900/70"
        }`}
      >
        <HiX className="h-7 w-7" />
      </motion.div>
      <p className="absolute inset-x-0 text-center text-xs font-medium text-white/90" style={{ bottom: 8 }}>
        {active ? "Release to close" : "Drag here to close"}
      </p>
    </motion.div>
  );
}

// Open chats float as chat heads wherever the user puts them; the expanded one opens as a card beside
// its head (on the side with more room), or above/below it when there's no room beside it.
function ChatDock() {
  const { windows, pageMode, closeWindow, toggleMinimize, conversations } = useChat();
  const navigate = useNavigate();
  const viewport = useViewportSize();
  const mobile = useIsMobile();
  const [saved, setSaved] = useState(readPositions);
  const [draggingId, setDraggingId] = useState(null);
  const [overClose, setOverClose] = useState(false);
  const overCloseRef = useRef(false);

  const openHeads = useMemo(() => windows.filter((w) => conversations[w.id]), [windows, conversations]);
  const ids = openHeads.map((w) => w.id);
  const positions = layoutHeads(ids, saved, viewport.width, viewport.height, mobile ? TAB_BAR_CLEARANCE : MARGIN);

  // Remember the spots of open heads. Closed chats keep theirs (a reopened chat goes back to its spot),
  // up to the most recent MAX_REMEMBERED.
  const idsKey = ids.join(",");
  useEffect(() => {
    if (!idsKey) return;
    setSaved((prev) => {
      const open = layoutHeads(idsKey.split(",").map(Number), prev, window.innerWidth, window.innerHeight);
      const closed = Object.entries(prev).filter(([id]) => !open[id]);
      const next = Object.fromEntries([...closed.slice(-(MAX_REMEMBERED - Object.keys(open).length)), ...Object.entries(open)]);
      const changed = JSON.stringify(next) !== JSON.stringify(prev);
      if (changed) savePositions(next);
      return changed ? next : prev;
    });
  }, [idsKey]);

  const onDragStart = useCallback((id) => {
    setDraggingId(id);
    overCloseRef.current = false;
    setOverClose(false);
  }, []);

  const onDragMove = useCallback((center) => {
    const target = closeTargetCenter();
    const near = Math.hypot(center.x - target.x, center.y - target.y) < CLOSE_RADIUS;
    if (near !== overCloseRef.current) {
      overCloseRef.current = near;
      setOverClose(near);
    }
    return near;
  }, []);

  const onDrop = useCallback(
    (id, { center, closing }) => {
      setDraggingId(null);
      setOverClose(false);
      overCloseRef.current = false;
      if (closing) {
        closeWindow(id);
        return;
      }
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const side = center.x < vw / 2 ? "left" : "right";
      const offset = side === "left" ? center.x - HEAD_SIZE / 2 : vw - center.x - HEAD_SIZE / 2;
      const position = placeOnScreen({ side, offset: Math.round(offset), bottom: Math.round(vh - center.y - HEAD_SIZE / 2) }, vw, vh);
      setSaved((prev) => {
        const next = { ...prev, [id]: position };
        savePositions(next);
        return next;
      });
    },
    [closeWindow]
  );

  // Phones: an open chat is full screen; keep its header in view when the keyboard opens.
  const fullScreenId = mobile && !pageMode ? openHeads.find((w) => !w.minimized)?.id : undefined;
  useFullScreenView(Boolean(fullScreenId));
  // Back returns to the chat bubbles, like the arrow in the chat header.
  useBackToClose(Boolean(fullScreenId), () => fullScreenId && toggleMinimize(fullScreenId));

  if (pageMode || !openHeads.length) return null;
  const active = openHeads.find((w) => !w.minimized);
  const activePos = active && positions[active.id];

  // The chat window opens beside its head, on the side with more room, level with it and kept on screen.
  // Without room beside it (narrow screens) it opens above or below the head instead.
  // Phones: the open chat fills the screen (back returns to the bubbles).
  let windowStyle = null;
  if (activePos && mobile) {
    windowStyle = { transformOrigin: "bottom center" };
  } else if (activePos) {
    const { width: vw, height: vh } = viewport;
    const left = leftOf(activePos, vw);
    const { bottom } = activePos;
    const windowWidth = Math.min(360, vw - 2 * EDGE);
    const roomRight = vw - (left + HEAD_SIZE) - GAP - EDGE;
    const roomLeft = left - GAP - EDGE;
    if (Math.max(roomRight, roomLeft) >= windowWidth) {
      const height = Math.min(WINDOW_HEIGHT, vh - 2 * EDGE);
      const toRight = roomRight >= roomLeft;
      windowStyle = {
        ...(toRight ? { left: left + HEAD_SIZE + GAP } : { right: vw - left + GAP }),
        bottom: clamp(bottom, EDGE, vh - height - EDGE),
        height,
        transformOrigin: `bottom ${toRight ? "left" : "right"}`,
      };
    } else {
      const spaceAbove = vh - bottom - HEAD_SIZE - GAP - EDGE;
      const spaceBelow = bottom - GAP - EDGE;
      const opensAbove = spaceAbove >= WINDOW_HEIGHT || spaceAbove >= spaceBelow;
      windowStyle = {
        left: clamp(left + HEAD_SIZE / 2 - windowWidth / 2, EDGE, vw - windowWidth - EDGE),
        ...(opensAbove ? { bottom: bottom + HEAD_SIZE + GAP } : { top: vh - bottom + GAP }),
        height: Math.min(WINDOW_HEIGHT, opensAbove ? spaceAbove : spaceBelow),
        transformOrigin: opensAbove ? "bottom center" : "top center",
      };
    }
  }

  return createPortal(
    <>
      <AnimatePresence>
        {active && windowStyle && !draggingId && (
          <motion.div
            key={active.id}
            data-chat-window={active.id}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.94 }}
            transition={{ duration: 0.16 }}
            style={windowStyle}
            className={`z-[60] overflow-hidden bg-white dark:bg-gray-900 ${
              mobile
                ? "mobile-fullscreen"
                : "fixed w-[360px] max-w-[calc(100vw-2rem)] rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700"
            }`}
          >
            <ConversationView
              conversationId={active.id}
              variant="dock"
              onMinimize={() => toggleMinimize(active.id)}
              onClose={() => closeWindow(active.id)}
              onExpand={() => navigate("/admin/chat", { state: { conversationId: active.id } })}
              onBack={mobile ? () => toggleMinimize(active.id) : undefined}
            />
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>{draggingId && <CloseTarget key="close-target" active={overClose} />}</AnimatePresence>
      <AnimatePresence>
        {!(mobile && active) &&
          openHeads.map((w, i) => (
            <ChatHead
              key={w.id}
              conversationId={w.id}
              expanded={!w.minimized}
              position={positions[w.id]}
              viewport={viewport}
              tourTarget={i === 0}
              onToggle={() => toggleMinimize(w.id)}
              onClose={() => closeWindow(w.id)}
              onDragStart={onDragStart}
              onDragMove={onDragMove}
              onDrop={onDrop}
            />
          ))}
      </AnimatePresence>
    </>,
    document.body
  );
}

export default ChatDock;
